import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../services/api';
import './Footer.css';

const shopLinks = [
  { label: 'Art', href: '/art' },
  { label: 'Fragrance', href: '/fragrance' },
  { label: 'Body', href: '/body' },
  { label: 'Music', href: '/music' },
];

const infoLinks = [
  { label: 'About', href: '/about' },
  { label: 'Contact', href: '/contact' },
];

const legalLinks = [
  { label: 'Shipping', href: '/policies/shipping' },
  { label: 'Returns', href: '/policies/returns' },
  { label: 'Privacy Policy', href: '/policies/privacy' },
  { label: 'Terms of Service', href: '/policies/terms' },
];

const Footer: React.FC = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      try {
        await apiRequest('/api/newsletter', {
          method: 'POST',
          body: JSON.stringify({ email }),
        });
        setSubmitted(true);
      } catch {
        setSubmitted(true);
      }
    }
  };

  return (
    <footer className="footer">
      <div className="footer__inner container">
        {/* Top section */}
        <div className="footer__top">
          {/* Wordmark + social */}
          <div className="footer__brand">
            <Link to="/" className="footer__wordmark">Micah Lynise</Link>
            <p className="footer__tagline">Art you can see, hear, smell, and feel.</p>
            <div className="footer__social">
              <a href="https://instagram.com/bymicahlynise" target="_blank" rel="noopener noreferrer" className="footer__social-link" aria-label="Instagram">
                <InstagramIcon />
              </a>
              <a href="https://spotify.com" target="_blank" rel="noopener noreferrer" className="footer__social-link" aria-label="Spotify">
                <SpotifyIcon />
              </a>
              <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="footer__social-link" aria-label="YouTube">
                <YouTubeIcon />
              </a>
              <a href="mailto:hello@micahlynise.com" className="footer__social-link" aria-label="Email">
                <EmailIcon />
              </a>
            </div>
          </div>

          {/* Link columns */}
          <div className="footer__links">
            <div className="footer__col">
              <h3 className="footer__col-heading">Shop</h3>
              <ul className="footer__col-list">
                {shopLinks.map((l) => (
                  <li key={l.href}>
                    <Link to={l.href} className="footer__link">{l.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div className="footer__col">
              <h3 className="footer__col-heading">Info</h3>
              <ul className="footer__col-list">
                {infoLinks.map((l) => (
                  <li key={l.href}>
                    <Link to={l.href} className="footer__link">{l.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div className="footer__col">
              <h3 className="footer__col-heading">Legal</h3>
              <ul className="footer__col-list">
                {legalLinks.map((l) => (
                  <li key={l.href}>
                    <Link to={l.href} className="footer__link">{l.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Newsletter */}
          <div className="footer__newsletter">
            <h3 className="footer__newsletter-heading">Stay connected.</h3>
            <p className="footer__newsletter-sub">
              New releases, events, and studio notes.
            </p>
            {submitted ? (
              <p className="footer__newsletter-thanks">Thank you for joining.</p>
            ) : (
              <form className="footer__newsletter-form" onSubmit={handleSubmit}>
                <label htmlFor="footer-email" className="sr-only">Email address</label>
                <input
                  id="footer-email"
                  type="email"
                  className="footer__newsletter-input"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <button type="submit" className="footer__newsletter-btn">Join</button>
              </form>
            )}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="footer__bottom">
          <p className="footer__copyright">© {new Date().getFullYear()} Micah Lynise</p>
        </div>
      </div>
    </footer>
  );
};

/* ─── Icons ──────────────────────────────────────────────────────────────────── */
const InstagramIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

const SpotifyIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M8 11.5c2.5-1 5.5-1 8 0M7 14.5c3-1.2 7-1.2 10 0M9 17.5c2-0.8 5-0.8 7 0" />
  </svg>
);

const YouTubeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.97C18.88 4 12 4 12 4s-6.88 0-8.59.45A2.78 2.78 0 0 0 1.46 6.42 29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.44a2.78 2.78 0 0 0 1.95-1.97A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z" />
    <polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" />
  </svg>
);

const EmailIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

export default Footer;
